import { useEffect, useMemo, useRef, useState } from "react";
import type { ProjectListItem } from "@koc/shared";
import { Input } from "./ui.js";

// Simple type-to-filter project picker. Pick from the existing list; no auto-create.
// The VideoImportDialog has its own variant that surfaces "unmatched" CSV rows; this
// reusable one just mirrors KocPicker's contract for use as a filter selector.
export function ProjectPicker({
  projects,
  value,
  onChange,
  placeholder = "search project…",
}: {
  projects: ProjectListItem[];
  value: number | null | undefined;
  onChange: (projectId: number | null) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = value != null ? projects.find((p) => p.id === value) ?? null : null;

  useEffect(() => {
    if (selected) setSearch("");
  }, [selected]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return projects.slice(0, 20);
    return projects
      .filter((p) => p.account_name.toLowerCase().includes(s))
      .slice(0, 20);
  }, [search, projects]);

  const displayValue = selected ? selected.account_name : search;

  return (
    <div className="relative flex-1" ref={wrapRef}>
      <Input
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => {
          if (selected) onChange(null);
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {selected && (
        <button
          type="button"
          aria-label="clear project"
          onClick={() => { onChange(null); setSearch(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-sm leading-none"
        >
          ×
        </button>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-stone-300 bg-white shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-2 py-2 text-xs text-stone-500">
              No project matches “{search}”.
            </li>
          ) : (
            matches.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between px-2 py-1.5 hover:bg-stone-100 cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(p.id);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <span className="truncate">{p.account_name}</span>
                <span className="text-xs text-stone-500 ml-2 whitespace-nowrap">
                  {p.video_count} videos
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
